import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TimerIcon from '@mui/icons-material/Timer';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ScheduleIcon from '@mui/icons-material/Schedule';

/* ─────────────────────────────────────────────────────────────────────────────
   BookingSettingsSummary
   Composant réutilisable affichant un résumé ergonomique des paramètres
   de réservation d'un listing.

   Props :
     listing   – objet listing complet (booking_mode, stock_quantity,
                 capacity_default, booking_settings JSONB)
     variant   – 'card' (défaut, pour pages) | 'accordion' (rétractable)
     defaultExpanded – booléen, utilisé uniquement en mode accordion (défaut: false)
   ──────────────────────────────────────────────────────────────────────────── */

const WEEK_DAYS_LABELS = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const BOOKING_MODE_CONFIG = {
  date_range: {
    label: 'Date range',
    icon: <CalendarTodayIcon fontSize="small" />,
    color: 'primary',
    description: 'Booking via calendar (Day / Night)',
  },
  time_slot: {
    label: 'Time slots',
    icon: <AccessTimeIcon fontSize="small" />,
    color: 'success',
    description: 'Booking by exclusive time slot',
  },
  time_slot_capacity: {
    label: 'Shared slots',
    icon: <GroupIcon fontSize="small" />,
    color: 'info',
    description: 'Booking by shared slot with capacity',
  },
};

const UNIT_LABELS = {
  day: 'Day',
  hour: 'Hour',
  night: 'Night',
  slot: 'Slot',
  week: 'Week',
  month: 'Month',
};

export default function BookingSettingsSummary({
  listing,
  variant = 'card',
  defaultExpanded = false,
}) {
  const theme = useTheme();
  const bs = listing?.booking_settings || {};
  const bookingMode = listing?.booking_mode;
  const stockQuantity = listing?.stock_quantity ?? 1;
  const capacityDefault = listing?.capacity_default ?? bs?.capacity_default;

  const { t } = useTranslation('booking');

  const modeConfig = BOOKING_MODE_CONFIG[bookingMode] || null;
  const isDateRange = bookingMode === 'date_range';
  const isSlotBased = bookingMode === 'time_slot' || bookingMode === 'time_slot_capacity';
  const isShared = bookingMode === 'time_slot_capacity';
  const isNightsMode = bs.date_range_mode === 'nights';

  const openingHours = useMemo(() => {
    if (!Array.isArray(bs.opening_hours) || bs.opening_hours.length === 0) return [];
    return bs.opening_hours
      .slice()
      .sort((a, b) => a.day - b.day);
  }, [bs.opening_hours]);

  const getWeekdayLabel = (day) => {
    return t(`weekdays.${day}`, WEEK_DAYS_LABELS[day] || `Jour ${day}`);
  };

  const formatDurationLocal = (value, unit) => {
    const n = Number(value);
    if (!n || n <= 0) return '—';
    switch (unit) {
      case 'hour':
        return n === 1 ? t('duration.hour_one', '1 heure') : t('duration.hour_other', '{{count}} heures', { count: n });
        case 'slot':
          return n === 1 ? t('duration.slot_one', '1 créneau') : t('duration.slot_other', '{{count}} créneaux', { count: n });
      case 'day':
        return n === 1 ? t('duration.day_one', '1 jour') : t('duration.day_other', '{{count}} jours', { count: n });
      case 'night':
        return n === 1 ? t('duration.night_one', '1 nuitée') : t('duration.night_other', '{{count}} nuitées', { count: n });
      case 'week':
        return n === 1 ? t('duration.week_one', '1 semaine') : t('duration.week_other', '{{count}} semaines', { count: n });
      case 'month':
        return n === 1 ? t('duration.month_one', '1 mois') : t('duration.month_other', '{{count}} mois', { count: n });
      default:
        return `${n}`;
    }
  };

  const formatDurationRangeLocal = (min, max, unit) => {
    const minN = Number(min);
    const maxN = Number(max);
    if (minN > 0 && maxN > 0) return `${formatDurationLocal(minN, unit)} – ${formatDurationLocal(maxN, unit)}`;
    if (minN > 0) return `${t('settings.minLabel', 'min')} ${formatDurationLocal(minN, unit)}`;
    if (maxN > 0) return `${t('settings.maxLabel', 'max')} ${formatDurationLocal(maxN, unit)}`;
    return '—';
  };

  const keyChips = useMemo(() => {
    const chips = [];

    if (modeConfig) {
      chips.push({
        key: 'mode',
        icon: modeConfig.icon,
        label: t(`mode.${bookingMode}.label`, modeConfig.label),
        color: modeConfig.color,
        variant: 'filled',
      });
    }

    if (isDateRange) {
      chips.push({
        key: 'dateRangeMode',
        icon: isNightsMode ? <NightsStayIcon fontSize="small" /> : <WbSunnyIcon fontSize="small" />,
        label: isNightsMode ? t('settings.dateRangeMode.nights', 'Nuitées') : t('settings.dateRangeMode.days', 'Journées'),
        color: isNightsMode ? 'secondary' : 'warning',
        variant: 'filled',
      });
    }

    if (bs.unit) {
      chips.push({
        key: 'unit',
        icon: <TimerIcon fontSize="small" />,
        label: t('settings.unitLabel', { unit: t(`units.${bs.unit}`, { defaultValue: UNIT_LABELS[bs.unit] || bs.unit }), defaultValue: 'Unit: {{unit}}' }),
        color: 'default',
        variant: 'outlined',
      });
    }

    if (bs.min_duration || bs.max_duration) {
      chips.push({
        key: 'duration',
        label: t('settings.durationLabel', 'Duration: {{range}}', { range: formatDurationRangeLocal(bs.min_duration, bs.max_duration, bs.unit) }),
        color: 'default',
        variant: 'outlined',
      });
    }

    if (stockQuantity > 1) {
      chips.push({
        key: 'stock',
        icon: <Inventory2Icon fontSize="small" />,
        label: t('settings.stockLabel', `Stock : ${stockQuantity}`, { count: stockQuantity }),
        color: 'default',
        variant: 'outlined',
      });
    }

    if (isShared && capacityDefault && capacityDefault >= 2) {
      chips.push({
        key: 'capacity',
        icon: <GroupIcon fontSize="small" />,
        label: t('settings.placesPerSlot', { count: capacityDefault, defaultValue: '{{count}} places/créneau' }),
        color: 'default',
        variant: 'outlined',
      });
    }

    if (isSlotBased && bs.slot_duration) {
      chips.push({
        key: 'slot',
        icon: <ScheduleIcon fontSize="small" />,
        label: t('settings.slotLabel', { duration: formatMinutes(bs.slot_duration), defaultValue: `Créneau : ${formatMinutes(bs.slot_duration)}` }),
        color: 'default',
        variant: 'outlined',
      });
    }

    if (isSlotBased) {
      const bBefore = Number(bs.buffer_before || 0);
      const bAfter = Number(bs.buffer_after || 0);
      if (bBefore > 0 || bAfter > 0) {
        chips.push({
          key: 'buffers',
          label: t('settings.buffers', 'Buffer: +{{before}} / +{{after}}', {
            before: formatMinutes(bBefore),
            after: formatMinutes(bAfter),
          }),
          color: 'default',
          variant: 'outlined',
        });
      }

      chips.push({
        key: 'opening',
        label: openingHours.length === 0 ? t('settings.opening.all_day', '24h/24, 7j/7') : t('settings.opening.defined', 'Horaires : définis'),
        color: 'default',
        variant: 'outlined',
      });
    }

    return chips;
  }, [
    modeConfig,
    isDateRange,
    isNightsMode,
    bs.unit,
    bs.min_duration,
    bs.max_duration,
    bs.slot_duration,
    bs.buffer_before,
    bs.buffer_after,
    isSlotBased,
    isShared,
    stockQuantity,
    capacityDefault,
    openingHours.length,
  ]);

  // ── Ligne d'info réutilisable ──────────────────────────────────────────────
  const InfoRow = ({ icon, label, value, chipColor, chipVariant }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      {icon && (
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      {chipColor ? (
        <Chip
          label={value}
          size="small"
          color={chipColor}
          variant={chipVariant || 'outlined'}
          sx={{ fontWeight: 600 }}
        />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      )}
    </Box>
  );

  // ── Contenu principal ──────────────────────────────────────────────────────
  const content = (
    <Box>
      {/* Résumé scan-friendly */}
      {modeConfig && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t(`mode.${bookingMode}.description`, modeConfig.description)}
          </Typography>
      )}

      {keyChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {keyChips.map((c) => (
            <Chip
              key={c.key}
              icon={c.icon}
              label={c.label}
              size="small"
              color={c.color}
              variant={c.variant}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {!bookingMode && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
          Aucun mode de réservation configuré.
        </Typography>
      )}

      {bookingMode && (
        <>
          <Divider sx={{ mb: 1.5 }} />

          {/* ── Unité & mode date_range ─────────────────────────────────────── */}
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {bs.unit && (
              <Grid item xs={6}>
                <InfoRow
                  icon={<TimerIcon fontSize="small" />}
                  label={t('settings.unit', 'Unité')}
                  value={t(`units.${bs.unit}`, UNIT_LABELS[bs.unit] || bs.unit)}
                  chipColor="default"
                />
              </Grid>
            )}

            {isDateRange && (
              <Grid item xs={6}>
                <InfoRow
                  icon={isNightsMode ? <NightsStayIcon fontSize="small" /> : <WbSunnyIcon fontSize="small" />}
                  label={t('settings.mode', 'Mode')}
                  value={isNightsMode ? t('settings.dateRangeMode.nights', 'Nuitées') : t('settings.dateRangeMode.days', 'Journées')}
                  chipColor={isNightsMode ? 'secondary' : 'warning'}
                  chipVariant="filled"
                />
              </Grid>
            )}
          </Grid>

          {/* ── Stock & Capacité ────────────────────────────────────────────── */}
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            {stockQuantity > 1 && (
              <Grid item xs={6}>
                <InfoRow
                  icon={<Inventory2Icon fontSize="small" />}
                  label={t('settings.stock', 'Stock')}
                  value={`${stockQuantity}`}
                />
              </Grid>
            )}

            {isShared && capacityDefault && capacityDefault >= 2 && (
              <Grid item xs={6}>
                <InfoRow
                  icon={<GroupIcon fontSize="small" />}
                  label={t('settings.places', 'Places')}
                  value={`${capacityDefault} / ${t('settings.perSlot', 'créneau')}`}
                />
              </Grid>
            )}
          </Grid>

          {/* ── Durées min / max ────────────────────────────────────────────── */}
          {(bs.min_duration || bs.max_duration) && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
                {t('settings.durationHeading', 'Durée')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {bs.min_duration && (
                  <Grid item xs={6}>
                    <InfoRow label={t('settings.minLabel', 'Min')} value={formatDurationLocal(bs.min_duration, bs.unit)} />
                  </Grid>
                )}
                {bs.max_duration && (
                  <Grid item xs={6}>
                    <InfoRow label={t('settings.maxLabel', 'Max')} value={formatDurationLocal(bs.max_duration, bs.unit)} />
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* ── Créneaux (slot-based uniquement) ───────────────────────────── */}
          {isSlotBased && bs.slot_duration && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
                {t('settings.slotsHeading', 'Créneaux')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={12}>
                  <InfoRow
                    icon={<ScheduleIcon fontSize="small" />}
                    label={t('settings.slotDuration', 'Durée créneau')}
                    value={formatMinutes(bs.slot_duration)}
                  />
                </Grid>
                {(bs.buffer_before > 0 || bs.buffer_after > 0) && (
                  <>
                    {bs.buffer_before > 0 && (
                      <Grid item xs={6}>
                        <InfoRow label={t('settings.before', 'Avant')} value={formatMinutes(bs.buffer_before)} />
                      </Grid>
                    )}
                    {bs.buffer_after > 0 && (
                      <Grid item xs={6}>
                        <InfoRow label={t('settings.after', 'Après')} value={formatMinutes(bs.buffer_after)} />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}

          {/* ── Prix par créneau ────────────────────────────────────────── */}
          {isSlotBased && (bs.price_per_slot || bs.base_price) && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
                {t('settings.pricingHeading', 'Tarif')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                {bs.price_per_slot && (
                  <Grid item xs={6}>
                    <InfoRow
                      label={t('settings.pricePerSlot', 'Prix / créneau')}
                      value={`${Number(bs.price_per_slot).toLocaleString()} ${listing?.currency || ''}`}
                      chipColor="success"
                      chipVariant="outlined"
                    />
                  </Grid>
                )}
                {bs.base_price && !bs.price_per_slot && (
                  <Grid item xs={6}>
                    <InfoRow
                      label={t('settings.basePrice', 'Prix de base')}
                      value={`${Number(bs.base_price).toLocaleString()} ${listing?.currency || ''}`}
                      chipColor="success"
                      chipVariant="outlined"
                    />
                  </Grid>
                )}
                {bs.pricing_mode && (
                  <Grid item xs={6}>
                    <InfoRow
                      label={t('settings.mode', 'Mode')}
                      value={
                        bs.pricing_mode === 'per_unit'
                          ? t('settings.pricingMode.per_unit', 'Par unité')
                          : bs.pricing_mode === 'per_seat'
                          ? t('settings.pricingMode.per_seat', 'Par place')
                          : bs.pricing_mode === 'per_unit_per_seat'
                          ? t('settings.pricingMode.per_unit_per_seat', 'Unité × place')
                          : bs.pricing_mode === 'flat'
                          ? t('settings.pricingMode.flat', 'Forfait')
                          : bs.pricing_mode
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* ── Horaires d'ouverture ───────────────────────────────────────── */}
          {isSlotBased && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
                {t('settings.openingHeading', 'Horaires')}
              </Typography>

              {openingHours.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('settings.opening.all_day', '24h/24, 7j/7')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {openingHours.map((h, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      variant="outlined"
                      label={`${getWeekdayLabel(h.day)} ${h.from}–${h.to}`}
                      sx={{
                        fontSize: '0.75rem',
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      }}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );

  // ── Rendu selon le variant ─────────────────────────────────────────────────
  if (variant === 'accordion') {
    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{
          boxShadow: theme.shadows[1],
          '&:before': { display: 'none' },
          borderRadius: '8px !important',
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.text.primary,
            minHeight: 44,
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              my: 0.5,
              gap: 1,
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('settings.title', 'Paramètres de réservation')}
              </Typography>
            </Box>

            {/* Infos clés visibles sans ouvrir */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {keyChips.slice(0, 4).map((c) => (
                <Chip
                  key={c.key}
                  size="small"
                  icon={c.icon}
                  label={c.label}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  }}
                />
              ))}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {content}
        </AccordionDetails>
      </Accordion>
    );
  }

  // variant === 'card' (défaut)
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        boxShadow: theme.shadows[1],
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <SettingsIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('settings.title', 'Paramètres de réservation')}
        </Typography>
      </Box>
      {content}
    </Box>
  );
}

/* ── Helpers de formatage ──────────────────────────────────────────────────── */

function formatDuration(value, unit) {
  const n = Number(value);
  if (!n || n <= 0) return '—';
  switch (unit) {
    case 'hour':
      return n === 1 ? '1 heure' : `${n} heures`;
    case 'day':
      return n === 1 ? '1 jour' : `${n} jours`;
    case 'night':
      return n === 1 ? '1 nuitée' : `${n} nuitées`;
    case 'week':
      return n === 1 ? '1 semaine' : `${n} semaines`;
    case 'month':
      return n === 1 ? '1 mois' : `${n} mois`;
    default:
      return `${n}`;
  }
}

function formatMinutes(value) {
  const m = Number(value);
  if (!m || m <= 0) return '0 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (rest === 0) return h === 1 ? '1h' : `${h}h`;
  return `${h}h${String(rest).padStart(2, '0')}`;
}

function formatDurationRange(min, max, unit) {
  const minN = Number(min);
  const maxN = Number(max);
  if (minN > 0 && maxN > 0) return `${formatDuration(minN, unit)} – ${formatDuration(maxN, unit)}`;
  if (minN > 0) return `${i18n.t('settings.minLabel', 'min')} ${formatDuration(minN, unit)}`;
  if (maxN > 0) return `${i18n.t('settings.maxLabel', 'max')} ${formatDuration(maxN, unit)}`;
  return '—';
}
